import { getLatestYouTubeVideos } from '@domain/features/youtube/youtube-feature'
import {
  buildCanonicalYouTubeUrl,
  extractYouTubeVideoId,
} from '@domain/features/youtube/youtube-url'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import type { YouTubeVideo } from '@domain/models/youtube'
import type { Webinar } from '@domain/models/webinar'
import { isAdmin, loadCurrentUser } from '@domain/features/user/user-feature'
import { AuthenticationService } from '@core/services/authentication/authentication-service'
import {
  createNewWebinar,
  getAllPastWebinars,
  getAllUpcomingWebinars,
  getWebinar,
  getWebinarByYouTubeUrl,
} from '@domain/features/webinars/webinars-feature'

export interface SyncPastWebinarsWithYouTubeOptions {
  count?: number
  offset?: number
  allowCreateIfAdmin?: boolean
}

export interface SyncPastWebinarsWithYouTubeResult {
  webinars: Webinar[]
  hasMore: boolean
}

interface SyncContext {
  canCreate: boolean
}

const DEFAULT_WEBINAR_TIME_ZONE = 'UTC'
const DEFAULT_WEBINAR_DURATION_MINUTES = 60
const YOUTUBE_SYNC_MAX_FETCH_ATTEMPTS = 4
const YOUTUBE_SYNC_BATCH_PADDING = 12

function normalizeNonNegativeInteger(value: number | undefined, fallback: number): number {
  if (!Number.isFinite(value)) {
    return fallback
  }

  const normalized = Math.floor(value ?? fallback)
  return normalized >= 0 ? normalized : fallback
}

function toWebinarTimeFromPublishedDate(publishedAt: Date): string {
  // Keep time aligned with the stored timestamp by using UTC consistently.
  return publishedAt.toISOString().slice(11, 16)
}

function isEligibleYouTubeWebinarVideo(video: YouTubeVideo): boolean {
  // Fail safe: only explicitly non-Short videos can become webinar candidates.
  return video.isShort === false
}

async function fetchEligibleYouTubeVideos(requiredEligibleCount: number): Promise<YouTubeVideo[]> {
  const normalizedRequiredCount = Math.max(1, requiredEligibleCount)
  let fetchCount = normalizedRequiredCount + YOUTUBE_SYNC_BATCH_PADDING
  let previousTotalVideosCount = -1

  for (let attempt = 0; attempt < YOUTUBE_SYNC_MAX_FETCH_ATTEMPTS; attempt += 1) {
    const videos = await getLatestYouTubeVideos(fetchCount)
    const eligibleVideos = videos.filter(isEligibleYouTubeWebinarVideo)
    if (eligibleVideos.length >= normalizedRequiredCount) {
      return eligibleVideos
    }

    if (videos.length === 0 || videos.length === previousTotalVideosCount || videos.length < fetchCount) {
      return eligibleVideos
    }

    previousTotalVideosCount = videos.length
    fetchCount += normalizedRequiredCount + YOUTUBE_SYNC_BATCH_PADDING
  }

  const fallbackVideos = await getLatestYouTubeVideos(fetchCount)
  return fallbackVideos.filter(isEligibleYouTubeWebinarVideo)
}

async function canCreateMissingWebinars(allowCreateIfAdmin: boolean): Promise<boolean> {
  if (!allowCreateIfAdmin) {
    return false
  }

  if (!AuthenticationService.getInstance().isUserLoggedIn) {
    return false
  }

  try {
    const currentUser = await loadCurrentUser()
    return isAdmin(currentUser)
  } catch {
    return false
  }
}

function indexWebinarsByVideoId(webinars: Webinar[]): Map<string, Webinar> {
  const byVideoId = new Map<string, Webinar>()
  for (const webinar of webinars) {
    const videoId = extractYouTubeVideoId(webinar.youtubeUrl)
    if (!videoId || byVideoId.has(videoId)) {
      continue
    }
    byVideoId.set(videoId, webinar)
  }
  return byVideoId
}

async function resolveWebinarForVideo(
  video: YouTubeVideo,
  byVideoId: Map<string, Webinar>,
  syncContext: SyncContext,
): Promise<Webinar | null> {
  if (!isEligibleYouTubeWebinarVideo(video)) {
    return null
  }

  const fromMap = byVideoId.get(video.id)
  if (fromMap) {
    return fromMap
  }

  const canonicalUrl = buildCanonicalYouTubeUrl(video.id)

  const matchedByUrl = await getWebinarByYouTubeUrl(canonicalUrl)
  if (matchedByUrl) {
    byVideoId.set(video.id, matchedByUrl)
    return matchedByUrl
  }

  if (!syncContext.canCreate) {
    return null
  }

  try {
    const createdId = await createNewWebinar({
      title: video.title,
      webinarDate: video.publishedAt,
      webinarTime: toWebinarTimeFromPublishedDate(video.publishedAt),
      timeZone: DEFAULT_WEBINAR_TIME_ZONE,
      durationMinutes: DEFAULT_WEBINAR_DURATION_MINUTES,
      youtubeUrl: canonicalUrl,
    })
    const createdWebinar = await getWebinar(createdId)
    byVideoId.set(video.id, createdWebinar)
    return createdWebinar
  } catch (error) {
    // If Firestore rejects writes, avoid spamming the console with repeated attempts.
    if (
      error instanceof CoreError &&
      error.cause === CoreErrorCause.PermissionDenied
    ) {
      syncContext.canCreate = false
    }

    const retriedMatch = await getWebinarByYouTubeUrl(canonicalUrl)
    if (retriedMatch) {
      byVideoId.set(video.id, retriedMatch)
      return retriedMatch
    }
    return null
  }
}

export class WebinarsYouTubeSyncFeature {
  public static async getSyncedPastWebinars(
    options: SyncPastWebinarsWithYouTubeOptions = {},
  ): Promise<SyncPastWebinarsWithYouTubeResult> {
    const count = Math.max(1, normalizeNonNegativeInteger(options.count, 4))
    const offset = normalizeNonNegativeInteger(options.offset, 0)
    const allowCreateIfAdmin = options.allowCreateIfAdmin ?? true

    const requiredEligibleCount = offset + count + 1
    const [youtubeVideos, pastWebinars, upcomingWebinars, canCreate] = await Promise.all([
      fetchEligibleYouTubeVideos(requiredEligibleCount),
      getAllPastWebinars(),
      getAllUpcomingWebinars(),
      canCreateMissingWebinars(allowCreateIfAdmin),
    ])

    if (youtubeVideos.length === 0) {
      const fallbackWebinars = pastWebinars.slice(offset, offset + count)
      return {
        webinars: fallbackWebinars,
        hasMore: pastWebinars.length > offset + count,
      }
    }

    const webinarsByVideoId = indexWebinarsByVideoId([
      ...pastWebinars,
      ...upcomingWebinars,
    ])
    const reconciledWebinars: Webinar[] = []
    const seenWebinarIds = new Set<string>()
    const syncContext: SyncContext = { canCreate }
    let scanIndex = offset

    while (scanIndex < youtubeVideos.length && reconciledWebinars.length < count) {
      const video = youtubeVideos[scanIndex]
      scanIndex += 1
      if (!video) {
        continue
      }

      const webinar = await resolveWebinarForVideo(video, webinarsByVideoId, syncContext)
      if (webinar) {
        if (seenWebinarIds.has(webinar.uid)) {
          continue
        }
        seenWebinarIds.add(webinar.uid)
        reconciledWebinars.push(webinar)
      }
    }

    const hasMore = scanIndex < youtubeVideos.length

    return {
      webinars: reconciledWebinars,
      hasMore,
    }
  }
}

export async function getSyncedPastWebinars(
  options: SyncPastWebinarsWithYouTubeOptions = {},
): Promise<SyncPastWebinarsWithYouTubeResult> {
  return WebinarsYouTubeSyncFeature.getSyncedPastWebinars(options)
}
